/** Ported VERBATIM from digitaladmin's ui/src/components/ErrorBoundary.tsx
 *  (Step 40). A React error boundary catches render-time exceptions that
 *  would otherwise blank the whole app to a white screen — the queue's
 *  loadError+Retry treatment only covers DATA-fetch failures, not a bug in
 *  the render path itself. Mounted once, at the app root. */
import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isDbError =
      error.message.includes('500') ||
      error.message.includes('ECONNREFUSED') ||
      error.message.includes('ER_') ||
      error.message.toLowerCase().includes('database') ||
      error.message.toLowerCase().includes('connect');

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-7 w-7 text-destructive" />
        </div>
        <div className="max-w-md space-y-1">
          <h2 className="text-lg font-semibold">
            {isDbError ? 'Database connection lost' : 'Something went wrong'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isDbError
              ? 'The server cannot reach the database. Check that MySQL is running and the connection details are correct.'
              : error.message}
          </p>
        </div>
        <button
          onClick={() => {
            this.setState({ error: null });
            window.location.reload();
          }}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Reload page
        </button>
        {!isDbError && (
          <details className="mt-2 w-full max-w-lg text-left">
            <summary className="cursor-pointer text-xs text-muted-foreground">Technical details</summary>
            <pre className="mt-2 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-3 text-xs">
              {error.stack ?? error.message}
            </pre>
          </details>
        )}
      </div>
    );
  }
}
